import type { AuthSession, UserRole, UserTier } from "@/lib/types";

/**
 * How much of the app a user sees. Admin comes from `role`, the other two from
 * profiles.tier. Display only: the one real permission (creating an
 * organization) is enforced by create_organization in the database, and RLS
 * decides what data anyone can read.
 */
export type ViewTier = "admin" | "pro" | "standard";

/** What not every tier sees. Anything not listed is visible to all. */
export type Capability =
  | "orgCreate"        // Create organization (also enforced in the DB)
  | "orgRequestJoin"   // "Request to join" on an org you're not in
  | "activity"         // profile activity calendar
  | "skillsRadar"      // profile skills radar
  | "fundWallet"       // Deposit / Withdraw
  | "onchainDetail"    // payout tx hash, explorer links, debug panel
  | "mintTestToken"    // test-USDT mint
  | "agents";          // Profile ▸ Ajustes ▸ AI agents

const VISIBLE_FROM: Record<Capability, ViewTier> = {
  orgCreate: "pro",
  orgRequestJoin: "pro",
  activity: "pro",
  skillsRadar: "pro",
  fundWallet: "pro",
  onchainDetail: "pro",
  mintTestToken: "admin",
  agents: "admin",  // admin-only until the spec's "Opening up" checklist passes
};

const RANK: Record<ViewTier, number> = { standard: 0, pro: 1, admin: 2 };

/**
 * An undefined tier means "not synced yet" (the session is not persisted, so
 * every page load starts here): show Standard rather than flash Pro features
 * at a Standard user. Once the sync answers, the session holds a real tier.
 */
export function viewTier(role: UserRole | undefined, tier: UserTier | undefined): ViewTier {
  if (role === "admin") return "admin";
  return tier === "pro" ? "pro" : "standard";
}

export function can(tier: ViewTier, cap: Capability): boolean {
  return RANK[tier] >= RANK[VISIBLE_FROM[cap]];
}

/** Narrow an untyped profiles row value to a tier, or undefined if absent/unknown. */
export function toUserTier(value: unknown): UserTier | undefined {
  return value === "standard" || value === "pro" ? value : undefined;
}

/**
 * The tier to put on a rebuilt session. usePrivySync rebuilds the session on
 * every effect run; like role, a tier we already know must survive that, and
 * only a real value from the profiles response may replace it.
 */
export function nextSessionTier(preserved: UserTier | undefined, fetched: unknown): UserTier | undefined {
  return toUserTier(fetched) ?? preserved;
}

/**
 * The tier once the first profiles sync has answered (or failed). A response
 * without a usable tier means the migration is not applied yet, and a failed
 * sync tells us nothing: both render today's app, so the frontend can ship
 * ahead of the migration and an outage never strips a member's page. Display
 * only -- the one real permission is enforced by create_organization.
 */
export function syncedTier(response: { tier?: unknown } | null | undefined): UserTier {
  return toUserTier(response?.tier) ?? "pro";
}

/**
 * The session after a profiles sync answers, or null when nothing changed.
 * Both syncs (initial and wallet re-sync) build on the session in the store
 * now, so whichever answers second keeps what the first delivered. Only the
 * first sync may settle an unknown tier (to syncedTier's fallback); the wallet
 * re-sync never invents one.
 */
export function sessionAfterProfileSync(
  current: AuthSession,
  response: { role?: string; tier?: unknown } | null | undefined,
  { firstSync }: { firstSync: boolean },
): AuthSession | null {
  const known = nextSessionTier(current.tier, response?.tier);
  const tier = firstSync ? known ?? syncedTier(response) : known;
  if (!response?.role && tier === current.tier) return null;
  return { ...current, role: (response?.role as UserRole | undefined) ?? current.role, tier };
}
